import { Zap, Bot, GitBranch, Play, Scale } from "lucide-react";
import type { WorkflowNodeType } from "@proclaw/shared";

/**
 * Left-rail palette: draggable chips that create new nodes on the canvas.
 * Each chip sets the drag data `application/reactflow` to its node type;
 * the canvas's onDrop handler reads that and instantiates the right node.
 *
 * Only rendered for admins.
 */
export function NodePalette() {
  return (
    <div
      data-tour="node-palette"
      className="flex w-[180px] shrink-0 flex-col gap-2 border-r border-border bg-surface p-3"
    >
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        Node Types
      </p>
      <PaletteChip
        type="trigger"
        label="Trigger"
        description="Starts the flow"
        color="green"
        Icon={Zap}
      />
      <PaletteChip
        type="agent"
        label="Agent"
        description="Calls an agent"
        color="accent"
        Icon={Bot}
      />
      <PaletteChip
        type="condition"
        label="Condition"
        description="Two or more paths"
        color="amber"
        Icon={GitBranch}
      />
      <PaletteChip
        type="action"
        label="Action"
        description="HTTP / email / post"
        color="purple"
        Icon={Play}
      />
      <PaletteChip
        type="decision"
        label="Decision"
        description="Classify or gate, fast"
        color="sky"
        Icon={Scale}
      />
      <p className="mt-3 text-[11px] leading-relaxed text-text-muted">
        Drag onto the canvas to add a node. Connect nodes by dragging from
        one handle to another.
      </p>
    </div>
  );
}

function PaletteChip({
  type,
  label,
  description,
  color,
  Icon,
}: {
  type: WorkflowNodeType;
  label: string;
  description: string;
  color: "green" | "accent" | "amber" | "purple" | "sky";
  Icon: typeof Zap;
}) {
  const colorClasses = {
    green: "bg-green-500/10 text-green-600 border-green-500/30",
    accent: "bg-accent/10 text-accent border-accent/30",
    amber: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    purple: "bg-purple-500/10 text-purple-500 border-purple-500/30",
    sky: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  }[color];
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/reactflow", type);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="flex cursor-grab items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 text-left transition-colors hover:border-accent active:cursor-grabbing"
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${colorClasses}`}
      >
        <Icon size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-text-primary">{label}</p>
        <p className="truncate text-[10px] text-text-muted">{description}</p>
      </div>
    </div>
  );
}
